import { useRef, useState } from "react";
import { Download, Share2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";

export const OracleCard = ({ message, emotion, avatarStyle, onClose }) => {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  const truncatedMessage = message.length > 280 ? message.slice(0, 277) + "..." : message;

  const handleDownload = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const w = 1080;
      const h = 1350;
      canvas.width = w;
      canvas.height = h;

      // Background
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, "#05050A");
      bg.addColorStop(0.5, "#0A0A12");
      bg.addColorStop(1, "#12121A");
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // Border frame
      ctx.strokeStyle = "rgba(212, 175, 55, 0.3)";
      ctx.lineWidth = 2;
      ctx.strokeRect(40, 40, w - 80, h - 80);

      // Inner border
      ctx.strokeStyle = "rgba(212, 175, 55, 0.1)";
      ctx.lineWidth = 1;
      ctx.strokeRect(56, 56, w - 112, h - 112);

      // Decorative corner elements
      const cornerSize = 30;
      ctx.strokeStyle = "rgba(212, 175, 55, 0.5)";
      ctx.lineWidth = 2;
      // Top-left
      ctx.beginPath(); ctx.moveTo(40, 70); ctx.lineTo(40, 40); ctx.lineTo(70, 40); ctx.stroke();
      // Top-right
      ctx.beginPath(); ctx.moveTo(w - 70, 40); ctx.lineTo(w - 40, 40); ctx.lineTo(w - 40, 70); ctx.stroke();
      // Bottom-left
      ctx.beginPath(); ctx.moveTo(40, h - 70); ctx.lineTo(40, h - 40); ctx.lineTo(70, h - 40); ctx.stroke();
      // Bottom-right
      ctx.beginPath(); ctx.moveTo(w - 70, h - 40); ctx.lineTo(w - 40, h - 40); ctx.lineTo(w - 40, h - 70); ctx.stroke();

      // Central orb glow
      const orb = ctx.createRadialGradient(w / 2, 380, 10, w / 2, 380, 200);
      orb.addColorStop(0, "rgba(212, 175, 55, 0.15)");
      orb.addColorStop(0.5, "rgba(212, 175, 55, 0.05)");
      orb.addColorStop(1, "transparent");
      ctx.fillStyle = orb;
      ctx.fillRect(0, 180, w, 400);

      // Title: KONSHUS
      ctx.fillStyle = "#D4AF37";
      ctx.font = "bold 18px Manrope, sans-serif";
      ctx.textAlign = "center";
      ctx.letterSpacing = "8px";
      ctx.fillText("K O N S H U S", w / 2, 160);

      // Decorative line
      ctx.strokeStyle = "rgba(212, 175, 55, 0.3)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(w / 2 - 120, 180); ctx.lineTo(w / 2 + 120, 180); ctx.stroke();

      // Oracle circle
      ctx.beginPath();
      ctx.arc(w / 2, 380, 100, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(212, 175, 55, 0.4)";
      ctx.lineWidth = 1.5;
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(w / 2, 380, 80, 0, Math.PI * 2);
      ctx.strokeStyle = "rgba(212, 175, 55, 0.2)";
      ctx.lineWidth = 1;
      ctx.stroke();

      // Inner filled orb
      const innerOrb = ctx.createRadialGradient(w / 2, 380, 0, w / 2, 380, 60);
      innerOrb.addColorStop(0, "rgba(212, 175, 55, 0.2)");
      innerOrb.addColorStop(1, "rgba(212, 175, 55, 0.02)");
      ctx.fillStyle = innerOrb;
      ctx.beginPath();
      ctx.arc(w / 2, 380, 60, 0, Math.PI * 2);
      ctx.fill();

      // Eye symbol in center
      ctx.fillStyle = "#D4AF37";
      ctx.font = "32px serif";
      ctx.textAlign = "center";
      ctx.fillText("\u25C7", w / 2, 390);

      // Emotion tag
      if (emotion) {
        ctx.fillStyle = "rgba(212, 175, 55, 0.6)";
        ctx.font = "italic 14px Manrope, sans-serif";
        ctx.fillText(`Detected: ${emotion}`, w / 2, 520);
      }

      // Divider
      ctx.strokeStyle = "rgba(240, 230, 210, 0.15)";
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(100, 560); ctx.lineTo(w - 100, 560); ctx.stroke();

      // Oracle message text (word-wrapped)
      ctx.fillStyle = "#F0F0F5";
      ctx.font = "20px Manrope, sans-serif";
      ctx.textAlign = "center";
      const words = truncatedMessage.split(" ");
      let line = "";
      let lineY = 620;
      const maxWidth = w - 200;
      for (const word of words) {
        const testLine = line + word + " ";
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line) {
          ctx.fillText(line.trim(), w / 2, lineY);
          line = word + " ";
          lineY += 34;
        } else {
          line = testLine;
        }
      }
      if (line) ctx.fillText(line.trim(), w / 2, lineY);

      // Bottom decorative
      ctx.strokeStyle = "rgba(240, 230, 210, 0.15)";
      ctx.beginPath(); ctx.moveTo(100, h - 200); ctx.lineTo(w - 100, h - 200); ctx.stroke();

      // Avatar style
      if (avatarStyle) {
        ctx.fillStyle = "rgba(148, 148, 168, 0.5)";
        ctx.font = "italic 13px Manrope, sans-serif";
        ctx.fillText(`Style: ${avatarStyle}`, w / 2, h - 170);
      }

      // Footer
      ctx.fillStyle = "rgba(148, 148, 168, 0.4)";
      ctx.font = "12px Manrope, sans-serif";
      ctx.fillText("ANATOMIA OCCULTI", w / 2, h - 100);
      ctx.font = "10px Manrope, sans-serif";
      ctx.fillStyle = "rgba(148, 148, 168, 0.3)";
      ctx.fillText("The Temple Speaks Through Those Who Listen", w / 2, h - 78);

      // Download
      const link = document.createElement("a");
      link.download = `konshus-oracle-${Date.now()}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Oracle card saved.");
    } catch {
      toast.error("Could not generate oracle card.");
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "Konshus Oracle Reading",
          text: truncatedMessage,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(truncatedMessage);
        toast.success("Reading copied to clipboard.");
      }
    } catch {
      // User cancelled share
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="oracle-card-overlay">
      <div className="relative max-h-[90vh] w-full max-w-md overflow-auto" ref={cardRef}>
        {/* Card Preview */}
        <div className="relative overflow-hidden rounded-[2rem] border border-primary/20 bg-gradient-to-b from-[#05050A] via-[#0A0A12] to-[#12121A] p-8">
          {/* Inner border */}
          <div className="absolute inset-4 rounded-[1.5rem] border border-primary/10 pointer-events-none" />

          {/* Orb glow */}
          <div className="absolute left-1/2 top-1/4 -translate-x-1/2 h-48 w-48 rounded-full bg-primary/10 blur-3xl" />

          <div className="relative space-y-6 text-center">
            <p className="text-xs font-bold uppercase tracking-[0.4em] text-primary">Konshus</p>
            <div className="mx-auto h-px w-32 bg-gradient-to-r from-transparent via-primary/30 to-transparent" />

            {/* Oracle circle */}
            <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full border border-primary/30">
              <div className="flex h-20 w-20 items-center justify-center rounded-full border border-primary/15 bg-primary/5">
                <span className="text-2xl text-primary">&loz;</span>
              </div>
            </div>

            {emotion && (
              <p className="text-xs italic text-primary/60">Detected: {emotion}</p>
            )}

            <div className="mx-auto h-px w-48 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

            <p className="text-sm leading-7 text-slate-200" data-testid="oracle-card-message">
              {truncatedMessage}
            </p>

            <div className="mx-auto h-px w-48 bg-gradient-to-r from-transparent via-white/10 to-transparent" />

            {avatarStyle && (
              <p className="text-xs italic text-slate-500">Style: {avatarStyle}</p>
            )}

            <div className="space-y-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-500">Anatomia Occulti</p>
              <p className="text-[9px] text-slate-600">The Temple Speaks Through Those Who Listen</p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="mt-4 flex gap-3 justify-center">
          <button
            className="ritual-button flex items-center gap-2 text-xs"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="oracle-card-download"
          >
            <Download className="h-3.5 w-3.5" />
            {downloading ? "Generating..." : "Save as Image"}
          </button>
          <button
            className="ghost-button flex items-center gap-2 text-xs"
            onClick={handleShare}
            data-testid="oracle-card-share"
          >
            <Share2 className="h-3.5 w-3.5" />
            Share
          </button>
          <button
            className="ghost-button text-xs"
            onClick={onClose}
            data-testid="oracle-card-close"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
